/**
 * CPODashboardWidget Component
 *
 * Reusable dashboard widget for CPO (Cost/Performance/Optimization) metrics.
 * Can be used in both V3 base dashboard and PowerSpec white-label dashboard.
 *
 * Features:
 * - Aggregated cost metrics across specifications
 * - Top recommendations summary
 * - Cost trend visualization
 * - Clickable for drill-down navigation
 *
 * Part of PowerSpec V3 - CPO Dashboard Integration
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Spinner, Alert, Progress } from 'flowbite-react';
import {
  HiCurrencyDollar,
  HiLightBulb,
  HiTrendingUp,
  HiTrendingDown,
  HiMinus,
  HiChartBar,
  HiExclamation,
  HiArrowRight
} from 'react-icons/hi';
import {
  getSpecMetrics,
  formatCost,
  type SpecMetrics
} from '../api/specifications';
import { isSuccessResponse } from '../api/apiClient';

interface CPODashboardWidgetProps {
  clientId: string;
  projectId?: string;
  onViewDetails?: () => void;
  compact?: boolean;
}

export const CPODashboardWidget: React.FC<CPODashboardWidgetProps> = ({
  clientId,
  projectId,
  onViewDetails,
  compact = false
}) => {
  const [metrics, setMetrics] = useState<SpecMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getSpecMetrics(clientId, projectId);

      if (isSuccessResponse(response)) {
        const metricsData = response.data?.Records?.[0];
        setMetrics(metricsData || null);
      } else {
        throw new Error(response.message || 'Failed to load metrics');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load CPO metrics';
      console.error('[CPODashboardWidget] Error:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [clientId, projectId]);

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId, fetchData]);

  if (loading) {
    return (
      <Card className={compact ? 'h-32' : 'h-64'}>
        <div className="flex items-center justify-center h-full">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert color="failure">
          <span>{error}</span>
        </Alert>
      </Card>
    );
  }

  if (!metrics) {
    return (
      <Card>
        <div className="text-center text-gray-500 py-4">
          <HiChartBar className="w-8 h-8 mx-auto text-gray-300 mb-2" />
          <p>No CPO metrics available</p>
        </div>
      </Card>
    );
  }

  // Calculate savings percentage
  const savingsPercentage = metrics.Total_Cost > 0
    ? ((metrics.Potential_Savings / metrics.Total_Cost) * 100).toFixed(1)
    : '0';

  if (compact) {
    // Compact version for smaller dashboard cards
    return (
      <Card
        className="cursor-pointer hover:shadow-lg transition-shadow"
        onClick={onViewDetails}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <HiCurrencyDollar className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Spec Costs (MTD)</div>
              <div className="text-xl font-bold text-gray-900">
                {formatCost(metrics.Total_Cost)}
              </div>
            </div>
          </div>
          <div className="text-right">
            {metrics.Active_Recommendations > 0 && (
              <Badge color="warning" className="mb-1">
                <HiLightBulb className="w-3 h-3 mr-1 inline" />
                {metrics.Active_Recommendations}
              </Badge>
            )}
            <div className="text-sm text-green-600">
              Save {formatCost(metrics.Potential_Savings)}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Full version for dashboard
  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <HiChartBar className="w-5 h-5 text-blue-600" />
          Specification CPO Metrics
        </h3>
        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            View Details <HiArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Total Cost */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <HiCurrencyDollar className="w-5 h-5 text-green-600" />
            <span className="text-sm text-gray-600">Total Cost</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCost(metrics.Total_Cost)}
          </div>
          <div className="text-xs text-gray-500">
            {(metrics.Total_Executions ?? 0).toLocaleString()} executions
          </div>
        </div>

        {/* Avg Per Spec */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <HiChartBar className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-gray-600">Avg per Spec</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCost(metrics.Avg_Cost_Per_Spec)}
          </div>
          <div className="text-xs text-gray-500">
            {metrics.Total_Specifications ?? 0} specifications
          </div>
        </div>

        {/* Active Recommendations */}
        <div className="p-4 bg-yellow-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <HiLightBulb className="w-5 h-5 text-yellow-600" />
            <span className="text-sm text-gray-600">Recommendations</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {metrics.Active_Recommendations}
          </div>
          <div className="text-xs text-gray-500">pending review</div>
        </div>

        {/* Potential Savings */}
        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <HiTrendingDown className="w-5 h-5 text-green-600" />
            <span className="text-sm text-gray-600">Potential Savings</span>
          </div>
          <div className="text-2xl font-bold text-green-600">
            {formatCost(metrics.Potential_Savings)}
          </div>
          <div className="text-xs text-gray-500">
            {savingsPercentage}% of total
          </div>
        </div>
      </div>

      {/* Top Cost Specs */}
      {metrics.Top_Cost_Specs && metrics.Top_Cost_Specs.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            <HiExclamation className="w-4 h-4 inline mr-1 text-yellow-500" />
            Top Cost Specifications
          </h4>
          <div className="space-y-2">
            {metrics.Top_Cost_Specs.slice(0, 3).map((spec, index) => {
              const percentage = (spec.Total_Cost / metrics.Total_Cost) * 100;
              return (
                <div key={spec.Specification_ID} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-gray-500 w-4">
                    {index + 1}.
                  </span>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-700 truncate max-w-[200px]">
                        {spec.Title}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCost(spec.Total_Cost)}
                      </span>
                    </div>
                    <Progress
                      progress={percentage}
                      size="sm"
                      color={index === 0 ? 'red' : index === 1 ? 'yellow' : 'blue'}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cost Trend Mini Chart */}
      {metrics.Cost_By_Period && metrics.Cost_By_Period.length > 0 && (
        <div className="pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Cost Trend (Last 7 Days)
          </h4>
          <div className="flex items-end justify-between h-16 gap-1">
            {metrics.Cost_By_Period.slice(-7).map((period, index) => {
              const maxCost = Math.max(...metrics.Cost_By_Period.map(p => p.cost));
              const height = maxCost > 0 ? (period.cost / maxCost) * 100 : 0;
              return (
                <div
                  key={period.period}
                  className="flex-1 flex flex-col items-center"
                >
                  <div
                    className="w-full bg-blue-500 rounded-t transition-all duration-300 hover:bg-blue-600"
                    style={{ height: `${Math.max(height, 4)}%` }}
                    title={`${period.period}: ${formatCost(period.cost)}`}
                  />
                  <span className="text-xs text-gray-400 mt-1">
                    {new Date(period.period).getDate()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

export default CPODashboardWidget;
